class ProductReviewSlider extends HTMLElement {
  #controller;
  #observer;
  #timer;
  #scrollTimer;
  #track;
  #slides = [];
  #dots = [];
  #index = 0;
  #visible = false;
  #paused = false;
  #motion;

  connectedCallback() {
    this.disconnectedCallback();
    this.#track = this.querySelector('.product-review-slider__track');
    this.#slides = [...this.querySelectorAll('.product-review-slider__slide')];
    this.#dots = [...this.querySelectorAll('[data-review-index]')];
    if (!this.#track || !this.#slides.length) return;
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#paused = this.#motion.matches;
    this.#index = 0;
    this.#track.scrollLeft = 0;
    this.#update();
    this.#syncTrackAriaLive();

    this.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-review-index]');
      if (dot && this.contains(dot)) {
        this.#pause();
        this.#select(Number(dot.dataset.reviewIndex));
      }
    }, options);
    this.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this.#pause();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.#slides.length - 1 : this.#index + (event.key === 'ArrowRight' ? 1 : -1);
      this.#select(next);
      if (event.target.matches('[data-review-index]')) this.#dots[this.#index]?.focus();
    }, options);
    this.#track.addEventListener('pointerdown', () => this.#pause(), options);
    this.#track.addEventListener('scroll', () => {
      clearTimeout(this.#scrollTimer);
      this.#scrollTimer = setTimeout(() => {
        const width = this.#track.clientWidth;
        if (width) this.#index = Math.max(0, Math.min(this.#slides.length - 1, Math.round(this.#track.scrollLeft / width)));
        this.#update();
      }, 100);
    }, { ...options, passive: true });
    this.addEventListener('mouseenter', () => clearTimeout(this.#timer), options);
    this.addEventListener('mouseleave', () => this.#schedule(), options);
    this.addEventListener('focusin', () => this.#pause(), options);
    document.addEventListener('visibilitychange', () => this.#schedule(), options);
    this.#motion.addEventListener('change', () => {
      if (this.#motion.matches) this.#pause();
    }, options);
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#schedule();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    clearTimeout(this.#scrollTimer);
  }

  #select(index) {
    this.#index = (index + this.#slides.length) % this.#slides.length;
    this.#track.scrollTo({ left: this.#index * this.#track.clientWidth, behavior: this.#motion.matches ? 'instant' : 'smooth' });
    this.#update();
  }

  #update() {
    this.#slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', String(index !== this.#index));
      slide.inert = index !== this.#index;
    });
    this.#dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === this.#index)));
  }

  #pause() {
    this.#paused = true;
    this.#syncTrackAriaLive();
    clearTimeout(this.#timer);
  }

  #syncTrackAriaLive() {
    this.#track.setAttribute('aria-live', this.dataset.autoplay === 'true' && !this.#paused ? 'off' : 'polite');
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (this.dataset.autoplay !== 'true' || this.#paused || !this.#visible || document.hidden || this.#slides.length < 2) return;
    const delay = Math.max(3000, Math.min(15000, Number(this.dataset.delay) || 5000));
    this.#timer = setTimeout(() => {
      this.#select(this.#index + 1);
      this.#schedule();
    }, delay);
  }
}

if (!customElements.get('product-review-slider')) customElements.define('product-review-slider', ProductReviewSlider);
